// src/hooks/useProfileActions.js
import { useCallback, useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { writeUserDocument } from '@services/firebaseUser';
import { getFirebase, getProdDb } from '@services/firebase';
import { useToast } from '../contexts/ToastContext';

const { auth, db } = getFirebase();

// Direct role detection without readGlobalProfiles filtering logic
const getRoleDirectly = async (userId) => {
  try {
    if (!userId) return 'User';
    const prodDb = getProdDb();
    const userDocRef = doc(prodDb, 'cUsers', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) return 'User';
    
    const userData = userDoc.data();
    if (userData.issuperuser === true) return 'Super';
    if (userData.isadminuser === true) return 'Admin';
    if (userData.ismover === true) return 'Mover';
    if (userData.isbuyer === true) return 'Buyer';
    
    return 'User';
  } catch (err) {
    console.error('Error getting role directly:', err);
    return 'User';
  }
};

export const useProfileActions = () => {
  const [user] = useAuthState(auth);
  const showToast = useToast();
  const [userRole, setUserRole] = useState(null);
  const [customProfiles, setCustomProfiles] = useState(undefined);
  const [favourites, setFavourites] = useState(undefined);
  const [enquiries, setEnquiries] = useState(undefined);

  // ── 1. Load user role ─────────────────────────────────────
  useEffect(() => {
    if (!user) {
      setUserRole(null);
      return;
    }

    const fetchRole = async () => {
      try {
        const role = await getRoleDirectly(user.uid);
        setUserRole(role);
      } catch (err) {
        console.error('Failed to load user role:', err);
        setUserRole('User');
      }
    };

    fetchRole();
  }, [user]);

  // ── 2. Real-time Custom Profiles ───────────────────────────
  useEffect(() => {
    if (!user || !db) {
      setCustomProfiles(undefined);
      return;
    }

    const q = query(collection(db, 'custom'), where('userId', '==', user.uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map(doc => ({
          id: doc.id,
          globalId: doc.data().globalId || doc.id,
          ...doc.data(),
        }));
        setCustomProfiles(data);
      },
      (err) => {
        console.error('Custom listener error:', err);
        setCustomProfiles([]);
      }
    );

    return unsub;
  }, [user]);

  // ── 3. Real-time Favourites ───────────────────────────────
  useEffect(() => {
    if (!user || !db) {
      setFavourites(undefined);
      return;
    }

    const q = query(collection(db, 'favourites'), where('userId', '==', user.uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map(doc => ({
          id: doc.id,
          globalId: doc.data().globalId || doc.id,
          ...doc.data(),
        }));
        setFavourites(data);
      },
      (err) => {
        console.error('Favourites listener error:', err);
        setFavourites([]);
      }
    );

    return unsub;
  }, [user]);

  // ── 4. Real-time Enquiries ────────────────────────────────
  useEffect(() => {
    if (!user || !db) {
      setEnquiries(undefined);
      return;
    }

    const q = query(collection(db, 'enquiry'), where('userId', '==', user.uid));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map(doc => ({
          id: doc.id,
          globalId: doc.data().globalId || doc.id,
          ...doc.data(),
        }));
        setEnquiries(data);
      },
      (err) => {
        console.error('Enquiries listener error:', err);
        setEnquiries([]);
      }
    );

    return unsub;
  }, [user]);

  // ── 5. Add to Custom ─────────────────────────────────────
  const addToCustom = useCallback(
    async (profile) => {
      if (!user) return false;

      const already = (customProfiles || []).some(p => p.globalId === profile.id);
      if (already) {
        showToast('Already in your custom list');
        return false;
      }

      const stripEuro = (v) => v != null ? String(v).replace(/[^0-9.]/g, '') || null : null;

      const cshapeHas3Tier = Array.isArray(profile.cshape) && profile.cshape.some(s => typeof s === 'string' && s.toLowerCase() === '3tier');
      const cshapeHasTier = Array.isArray(profile.cshape) && profile.cshape.some(s => typeof s === 'string' && s.toLowerCase().includes('tier'));
      const tier1Data = { tier: 1, cflavour: profile.cflavour || '', cfilling: profile.cfilling || '', sponge: profile.sponge || '' };
      const computedTiers = cshapeHas3Tier
        ? [tier1Data, { ...tier1Data, tier: 2 }, { ...tier1Data, tier: 3 }]
        : cshapeHasTier
        ? [tier1Data, { ...tier1Data, tier: 2 }]
        : [tier1Data];

      // Exclude redundant root-level flavour/filling/sponge fields — these live in the tiers array
      const { cflavour, cfilling, sponge, cflavour1, cfilling1, sponge1, ...restProfile } = profile;
      const profileData =
        userRole === 'User' || userRole === 'Buyer'
          ? {
              globalId: profile.id,
              eggless: false,
              glutenFree: false,
              quantity: null,
              isFromHome: true,
              ...restProfile,
              images: profile.cimage ? [profile.cimage] : (restProfile.images && restProfile.images.length > 0 ? restProfile.images : []),
              ccost: stripEuro(profile.ccost),
              costest: stripEuro(profile.costest),
              tiers: computedTiers,
              tierCount: computedTiers.length,
              multiTier: cshapeHasTier,
              moreTier: cshapeHas3Tier,
            }
          : { globalId: profile.id, isFromHome: true, ...restProfile, cflavour, cfilling, sponge, tiers: computedTiers, tierCount: computedTiers.length, multiTier: cshapeHasTier, moreTier: cshapeHas3Tier, ccost: stripEuro(profile.ccost), costest: stripEuro(profile.costest) };

      try {
        console.log('[addToCustom] Writing profileData:', JSON.stringify({ tiers: profileData.tiers, tierCount: profileData.tierCount, multiTier: profileData.multiTier, moreTier: profileData.moreTier, cshape: profileData.cshape }));
        await writeUserDocument('custom', user.uid, profileData);
        showToast('Added to custom!');
        return true;
      } catch (err) {
        console.error(err);
        showToast('Failed to add');
        return false;
      }
    },
    [user, customProfiles, userRole]
  );

  // ── 5. Add to Favourites ─────────────────────────────────
  const addToFavourites = useCallback(
    async (profile) => {
      if (!user) return false;

      const already = (favourites || []).some(p => p.globalId === profile.id);
      if (already) {
        showToast('Already in favourites');
        return false;
      }

      try {
        await writeUserDocument('favourites', user.uid, {
          globalId: profile.id,
          ...profile,
        });
        showToast('Added to favourites!');
        return true;
      } catch (err) {
        console.error(err);
        showToast('Failed to add');
        return false;
      }
    },
    [user, favourites]
  );

  // ── 7. Simple helper functions (NOT hooks!) ─────────────
  const isInCustom = (profileId) => (customProfiles || []).some(p => p.globalId === profileId);
  const isInFavourites = (profileId) => (favourites || []).some(p => p.globalId === profileId);
  const isInEnquiry = (profileId) => (enquiries || []).some(e => e.globalId === profileId);

  // ── Return everything ─────────────────────────────────────
  return {
    userRole,
    customProfiles,
    favourites,
    enquiries,
    addToCustom,
    addToFavourites,
    isInCustom,
    isInFavourites,
    isInEnquiry,
  };
};